import { v4 as uuidv4 } from 'uuid';
import type {
  CustomNode,
  JsonValue,
  MapBodySlot,
  NodeStruct,
} from '@/types/node-types';
import { ConnectionType, type CustomEdge } from '@/types/edge-types';
import { isMapNode } from './map-control';
import { nodeLabel } from './repeat-loop';
import {
  canBeInForEach,
  describeForEach,
  downstreamOf,
  forEachItemSlot,
  isForEachEdge,
  MAP_COLLECT_ROLE,
  MAP_COLLECT_TARGET_HANDLE,
  MAP_ITEM_ROLE,
  MAP_ITEM_SOURCE_HANDLE,
  MAP_REGION_VERSION,
  mapItemTargetHandleId,
  type ForEachRegion,
} from './for-each-region';
import { isBranchingStruct } from './functional-control-flow';

/**
 * The For-each body as one sentence with pickers:
 *
 *   For each [item] in [list], run [this part], collecting [the last node's output].
 *
 * `forEachSentenceFromGraph` reads it off the canvas; `planForEachEdges`
 * and `forEachAuthoredConfig` write it back as exactly what the backend
 * compiles: the item edge, the dashed collector edge, and a minimal
 * `map_config` (version 1.1, only what the user set; the server fills the
 * rest and compiles `region` at prepare).
 */

export interface ForEachSentence {
  entryId: string | null;
  exitId: string | null;
  /** Output port of the last step that is collected. */
  collectPortId: string | null;
  itemName: string;
}

export interface ForEachPickerOption {
  id: string;
  label: string;
  kind: string;
}

const struct = (node: CustomNode | undefined): NodeStruct | undefined =>
  node?.data?.struct;
const edgeSource = (edge: CustomEdge): string =>
  edge.source_node_id || edge.source;
const edgeTarget = (edge: CustomEdge): string =>
  edge.target_node_id || edge.target;

const isRecord = (value: unknown): value is Record<string, JsonValue> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const storedMapConfig = (
  carrier: CustomNode | undefined,
): Record<string, JsonValue> => {
  const value = struct(carrier)?.params?.find(
    (param) => param.field_id === 'map_config',
  )?.value;
  return isRecord(value) ? value : {};
};

export const forEachSentenceFromGraph = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  mapId: string,
): { sentence: ForEachSentence; region: ForEachRegion | null } => {
  const region = describeForEach(nodes, edges, mapId);
  const stored = storedMapConfig(region?.carrier);
  return {
    region,
    sentence: {
      entryId: region?.entryId ?? null,
      exitId: region?.regionMode ? region.exitId : null,
      collectPortId: region?.collectPortId ?? null,
      itemName:
        typeof stored.item_name === 'string' && stored.item_name
          ? stored.item_name
          : 'item',
    },
  };
};

/** The list the Map reads: the source wired into its Items input. */
export const forEachListSource = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  mapId: string,
): { name: string; port: string } | null => {
  const edge = edges.find(
    (candidate) =>
      edgeTarget(candidate) === mapId &&
      !isForEachEdge(candidate) &&
      candidate.edge_type !== ConnectionType.REPEAT &&
      candidate.edge_type !== ConnectionType.CONDITIONAL,
  );
  if (!edge) return null;
  const source = nodes.find((node) => node.id === edgeSource(edge));
  const port = struct(source)?.output_ports?.find(
    (candidate) => candidate.id === edge.sourceHandle,
  );
  return {
    name: nodeLabel(source),
    port: port?.display_name || port?.name || '',
  };
};

/** Nodes that can be the first step: they take an item. */
export const forEachEntryCandidates = (
  nodes: readonly CustomNode[],
  mapId: string,
  catalog?: readonly NodeStruct[],
): ForEachPickerOption[] =>
  nodes
    .filter(
      (node) =>
        node.id !== mapId &&
        !isMapNode(struct(node)) &&
        forEachItemSlot(struct(node), catalog) !== null,
    )
    .map((node) => ({
      id: node.id,
      label: nodeLabel(node),
      kind: struct(node)?.display_name ?? '',
    }));

/**
 * Nodes whose output can be collected: the first step itself, or a
 * map-safe node after it. With nothing after the first step yet, every
 * map-safe node is offered (picking one wires the first step into it).
 */
export const forEachExitCandidates = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  mapId: string,
  entryId: string | null,
  catalog?: readonly NodeStruct[],
): ForEachPickerOption[] => {
  const eligible = nodes.filter(
    (node) =>
      node.id !== mapId &&
      !isMapNode(struct(node)) &&
      !isBranchingStruct(struct(node)) &&
      canBeInForEach(struct(node), catalog),
  );
  let offered = eligible;
  if (entryId) {
    const downstream = downstreamOf(nodes, edges, entryId, mapId);
    const reachable = eligible.filter((node) => downstream.has(node.id));
    offered = reachable.length > 1 ? reachable : eligible;
  }
  return offered.map((node) => ({
    id: node.id,
    label: nodeLabel(node),
    kind: struct(node)?.display_name ?? '',
  }));
};

const PREFERRED_OUTPUTS = ['result', 'output', 'value', 'text'];

/** The output port collected by default: result/output/value, else the first. */
export const defaultCollectPort = (
  exit: CustomNode | undefined,
): string | null => {
  const ports = struct(exit)?.output_ports ?? [];
  for (const name of PREFERRED_OUTPUTS) {
    const port = ports.find((candidate) => candidate.name === name);
    if (port) return port.id;
  }
  return ports[0]?.id ?? null;
};

export const describeForEachSentence = (
  sentence: ForEachSentence,
  nodes: readonly CustomNode[],
  listLabel: string | null,
  region: ForEachRegion | null,
): string => {
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const name = (id: string | null) => (id ? nodeLabel(byId.get(id)) : '…');
  const steps = region?.regionIds.length ?? 0;
  const part =
    !sentence.entryId || !sentence.exitId
      ? '…'
      : sentence.entryId === sentence.exitId
        ? name(sentence.entryId)
        : `${name(sentence.entryId)} → ${name(sentence.exitId)}${
            steps > 2 ? ` (${steps} steps)` : ''
          }`;
  const exit = sentence.exitId ? byId.get(sentence.exitId) : undefined;
  const port = struct(exit)?.output_ports?.find(
    (candidate) => candidate.id === sentence.collectPortId,
  );
  const collected = sentence.exitId
    ? `${name(sentence.exitId)}\u2019s ${port?.name ?? 'output'}`
    : '…';
  return `For each ${sentence.itemName || 'item'} in ${
    listLabel ?? '…'
  }, run ${part}, collecting ${collected}.`;
};

// --- edges -------------------------------------------------------------------

export const buildMapItemEdge = (
  map: CustomNode,
  entry: CustomNode,
  slot: MapBodySlot,
  layoutType: string,
): CustomEdge => ({
  id: uuidv4(),
  source: map.id,
  sourceHandle: MAP_ITEM_SOURCE_HANDLE,
  target: entry.id,
  targetHandle: mapItemTargetHandleId(slot),
  sourceNodeId: struct(map)?.react_flow_generated_id,
  targetNodeId: struct(entry)?.react_flow_generated_id,
  source_node_id: map.id,
  source_port_id: MAP_ITEM_SOURCE_HANDLE,
  target_node_id: entry.id,
  target_port_id: slot.kind === 'input' ? slot.target_id : null,
  target_param_id: slot.kind === 'param' ? slot.target_id : null,
  type: layoutType,
  edge_type:
    slot.kind === 'param'
      ? ConnectionType.PORT_TO_PARAM
      : ConnectionType.PORT_TO_PORT,
  animated: false,
  data: { map_role: MAP_ITEM_ROLE },
});

export const buildMapCollectEdge = (
  exit: CustomNode,
  portId: string,
  map: CustomNode,
  layoutType: string,
): CustomEdge => ({
  id: uuidv4(),
  source: exit.id,
  sourceHandle: portId,
  target: map.id,
  targetHandle: MAP_COLLECT_TARGET_HANDLE,
  sourceNodeId: struct(exit)?.react_flow_generated_id,
  targetNodeId: struct(map)?.react_flow_generated_id,
  source_node_id: exit.id,
  source_port_id: portId,
  target_node_id: map.id,
  target_port_id: MAP_COLLECT_TARGET_HANDLE,
  target_param_id: null,
  type: layoutType,
  edge_type: ConnectionType.MAP_COLLECT,
  animated: false,
  data: { map_role: MAP_COLLECT_ROLE },
});

/** The data edge that feeds the first step's output into the last step's item input. */
const buildBodyLink = (
  entry: CustomNode,
  exit: CustomNode,
  catalog: readonly NodeStruct[] | undefined,
  layoutType: string,
): CustomEdge | null => {
  const output = struct(entry)?.output_ports?.[0];
  const slot = forEachItemSlot(struct(exit), catalog);
  if (!output || !slot) return null;
  const portId =
    slot.kind === 'param'
      ? struct(exit)?.params?.find((param) => param.field_id === slot.target_id)
          ?.bindable_config?.input_port?.id
      : struct(exit)?.input_ports?.find(
          (port) => port.name === slot.target_id || port.id === slot.target_id,
        )?.id;
  if (!portId) return null;
  return {
    id: uuidv4(),
    source: entry.id,
    sourceHandle: output.id,
    target: exit.id,
    targetHandle: portId,
    sourceNodeId: struct(entry)?.react_flow_generated_id,
    targetNodeId: struct(exit)?.react_flow_generated_id,
    source_node_id: entry.id,
    source_port_id: output.id,
    target_node_id: exit.id,
    target_port_id: slot.kind === 'input' ? portId : null,
    target_param_id: slot.kind === 'param' ? slot.target_id : null,
    type: layoutType,
    edge_type:
      slot.kind === 'param'
        ? ConnectionType.PORT_TO_PARAM
        : ConnectionType.PORT_TO_PORT,
    animated: false,
    data: {},
  };
};

export interface ForEachEdgePlan {
  add: CustomEdge[];
  removeIds: string[];
}

/** Edges to add and remove so the canvas says what the sentence says. */
export const planForEachEdges = (
  nodes: readonly CustomNode[],
  edges: readonly CustomEdge[],
  mapId: string,
  sentence: ForEachSentence,
  layoutType: string,
  catalog?: readonly NodeStruct[],
): ForEachEdgePlan => {
  const plan: ForEachEdgePlan = { add: [], removeIds: [] };
  const map = nodes.find((node) => node.id === mapId);
  const region = describeForEach(nodes, edges, mapId);
  if (!map || !region) return plan;
  const entry = sentence.entryId
    ? nodes.find((node) => node.id === sentence.entryId)
    : undefined;
  const exit = sentence.exitId
    ? nodes.find((node) => node.id === sentence.exitId)
    : undefined;

  // Item edge.
  const slot = entry ? forEachItemSlot(struct(entry), catalog) : null;
  const keepItem = region.itemEdges.find(
    (edge) =>
      edgeTarget(edge) === sentence.entryId &&
      edge.targetHandle === (slot ? mapItemTargetHandleId(slot) : null),
  );
  for (const edge of region.itemEdges) {
    if (edge !== keepItem) plan.removeIds.push(edge.id);
  }
  if (entry && slot && !keepItem) {
    plan.add.push(buildMapItemEdge(map, entry, slot, layoutType));
  }

  // Collector edge.
  const portId =
    exit &&
    (sentence.collectPortId &&
    struct(exit)?.output_ports?.some(
      (port) => port.id === sentence.collectPortId,
    )
      ? sentence.collectPortId
      : defaultCollectPort(exit));
  const keepCollect = region.collectEdges.find(
    (edge) =>
      edgeSource(edge) === sentence.exitId && edge.sourceHandle === portId,
  );
  for (const edge of region.collectEdges) {
    if (edge !== keepCollect) plan.removeIds.push(edge.id);
  }
  if (exit && portId && !keepCollect) {
    plan.add.push(buildMapCollectEdge(exit, portId, map, layoutType));
  }

  // The last step must read the first step's output.
  if (entry && exit && entry.id !== exit.id) {
    const downstream = downstreamOf(nodes, edges, entry.id, mapId);
    if (!downstream.has(exit.id)) {
      const link = buildBodyLink(entry, exit, catalog, layoutType);
      if (link) plan.add.push(link);
    }
  }
  return plan;
};

/** Remove the body: both For-each edges go; the Map returns to a single step. */
export const planClearForEach = (
  edges: readonly CustomEdge[],
  mapId: string,
): ForEachEdgePlan => ({
  add: [],
  removeIds: edges
    .filter(
      (edge) =>
        isForEachEdge(edge) &&
        (edgeSource(edge) === mapId || edgeTarget(edge) === mapId),
    )
    .map((edge) => edge.id),
});

/**
 * The authored `map_config` for a For-each body: what was saved before,
 * plus version 1.1, the semantic id and the item name. No catalog default
 * the user did not set is written, and never the compiled `region`.
 */
export const forEachAuthoredConfig = (
  carrier: CustomNode | undefined,
  mapId: string,
  itemName?: string,
): Record<string, JsonValue> => {
  const stored = storedMapConfig(carrier);
  const rest = { ...stored };
  delete rest.region;
  delete rest.runtime_limits;
  delete rest.item_name;
  const named = itemName !== undefined ? itemName : stored.item_name;
  return {
    ...rest,
    version: MAP_REGION_VERSION,
    semantic_id:
      typeof stored.semantic_id === 'string' && stored.semantic_id
        ? stored.semantic_id
        : `map:${mapId}`,
    ...(typeof named === 'string' && named && named !== 'item'
      ? { item_name: named }
      : {}),
    body: null,
  };
};

/** Back to a single-step Map (version 1.0): the user picks the step again. */
export const singleStepConfig = (
  carrier: CustomNode | undefined,
): Record<string, JsonValue> | null => {
  const stored = storedMapConfig(carrier);
  if (stored.version !== MAP_REGION_VERSION) return null;
  return { ...stored, version: '1.0', body: null };
};

export const applyForEachPlan = (
  edges: readonly CustomEdge[],
  plan: ForEachEdgePlan,
): CustomEdge[] => [
  ...edges.filter((edge) => !plan.removeIds.includes(edge.id)),
  ...plan.add,
];
